import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

const QuotaIndicator = ({ status, onUpgradeClick, compact = false }) => {
    const { t } = useLanguage();
    
    if (!status || !status.quotas) return null;
    
    const { quotas, tier, tierDescription } = status;
    
    const getBarColor = (percentage) => {
        if (percentage >= 100) return 'bg-red-500';
        if (percentage >= 80) return 'bg-amber-500';
        if (percentage >= 60) return 'bg-yellow-500';
        return 'bg-cyan-500';
    };
    
    const formatLimit = (limit) => {
        if (limit === -1) return '∞';
        if (limit === 0) return '0';
        return limit.toString();
    };
    
    const tierColors = {
        guest: 'text-slate-400',
        free: 'text-slate-300',
        pro: 'text-blue-400',
        premium: 'text-purple-400',
        enterprise: 'text-amber-400',
        business: 'text-purple-400'
    };

    if (compact) {
        return (
            <div className="flex items-center gap-3 text-xs">
                <span className={`font-medium ${tierColors[tier] || 'text-slate-400'}`}>
                    {tierDescription || tier}
                </span>
                <div className="flex items-center gap-2">
                    <QuotaChip 
                        label={t('banners.quota.analysesShort')} 
                        used={quotas.crux.used} 
                        limit={quotas.crux.limit}
                        percentage={quotas.crux.percentage}
                    />
                    {quotas.pagespeed.limit > 0 && (
                        <QuotaChip 
                            label="PSI" 
                            used={quotas.pagespeed.used} 
                            limit={quotas.pagespeed.limit}
                            percentage={quotas.pagespeed.percentage}
                        />
                    )}
                </div>
            </div>
        );
    }
    
    return (
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-3">
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    <span className="text-sm font-medium text-white">{t('banners.quota.dailyUsage')}</span>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${tier === 'business' ? 'bg-purple-500/20 text-purple-400' : tier === 'pro' ? 'bg-blue-500/20 text-blue-400' : 'bg-slate-700 text-slate-400'}`}>
                    {tierDescription}
                </span>
            </div>
            
            <div className="space-y-2">
                <QuotaBar 
                    label={t('banners.quota.cruxAnalyses')}
                    tooltip={t('banners.quota.cruxAnalysesTooltip')}
                    used={quotas.crux.used}
                    limit={quotas.crux.limit}
                    percentage={quotas.crux.percentage}
                    getBarColor={getBarColor}
                    formatLimit={formatLimit}
                    todayLabel={t('banners.quota.today')}
                />
                
                {quotas.pagespeed.limit !== 0 && (
                    <QuotaBar 
                        label={t('banners.quota.pageSpeed')}
                        tooltip={t('banners.quota.pageSpeedTooltip')}
                        used={quotas.pagespeed.used}
                        limit={quotas.pagespeed.limit}
                        percentage={quotas.pagespeed.percentage}
                        getBarColor={getBarColor}
                        formatLimit={formatLimit}
                        todayLabel={t('banners.quota.today')}
                    />
                )}
                
                <QuotaBar 
                    label={t('banners.quota.trackedDomains')}
                    tooltip={t('banners.quota.trackedDomainsTooltip')}
                    used={quotas.domains.used}
                    limit={quotas.domains.limit}
                    percentage={quotas.domains.percentage}
                    getBarColor={getBarColor}
                    formatLimit={formatLimit}
                    isTotal={true}
                />
            </div>
            
            {(tier === 'guest' || tier === 'free') && onUpgradeClick && (
                <button
                    onClick={onUpgradeClick}
                    className="mt-3 w-full py-1.5 text-sm text-cyan-400 hover:text-cyan-300 border border-cyan-500/30 hover:border-cyan-500/50 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    {t('banners.quota.increaseLimits')}
                </button>
            )}
        </div>
    );
};

const QuotaBar = ({ label, tooltip, used, limit, percentage, getBarColor, formatLimit, isTotal = false, todayLabel = 'today' }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const isUnlimited = limit === -1;
    const displayPercentage = isUnlimited ? 0 : Math.min(percentage, 100);
    
    return (
        <div>
            <div className="flex justify-between text-xs mb-1">
                <span className="text-slate-400 flex items-center gap-1 relative">
                    {label}
                    {tooltip && (
                        <span 
                            className="cursor-help"
                            onMouseEnter={() => setShowTooltip(true)}
                            onMouseLeave={() => setShowTooltip(false)}
                        >
                            <svg className="w-3 h-3 text-slate-500 hover:text-cyan-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {showTooltip && (
                                <span className="absolute left-0 top-5 z-50 w-48 px-2 py-1.5 text-xs bg-slate-900 border border-slate-600 rounded-lg shadow-xl text-slate-300 whitespace-normal">
                                    {tooltip}
                                </span>
                            )}
                        </span>
                    )}
                </span>
                <span className={percentage >= 80 ? 'text-amber-400' : 'text-slate-300'}>
                    {used} / {formatLimit(limit)}
                    {!isTotal && ` ${todayLabel}`}
                </span>
            </div>
            <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                {isUnlimited ? (
                    <div className="h-full w-full bg-gradient-to-r from-purple-500/50 to-purple-500/20" />
                ) : (
                    <div 
                        className={`h-full transition-all duration-500 ${getBarColor(percentage)}`}
                        style={{ width: `${displayPercentage}%` }}
                    />
                )}
            </div>
        </div>
    );
};

const QuotaChip = ({ label, used, limit, percentage }) => {
    const isUnlimited = limit === -1;
    const colorClass = isUnlimited 
        ? 'text-purple-400' 
        : percentage >= 100 
            ? 'text-red-400' 
            : percentage >= 80 
                ? 'text-amber-400' 
                : 'text-slate-300';
    
    return (
        <span className={`${colorClass}`}>
            {label}: {used}/{isUnlimited ? '∞' : limit}
        </span>
    );
};

export default QuotaIndicator;
